/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Coachmark, DirectionalHint, TeachingBubbleContent } from '@fluentui/react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';

const messages = defineMessages({
    ariaAlertText: { id: 'luis.reviewEndpointCoachmark.ariaAlertText', defaultMessage: 'A coachmark has appeared' },
    headline: { id: 'luis.reviewEndpointCoachmark.headline', defaultMessage: 'Label entities' },
    closeBtnAriaLabel: { id: 'luis.reviewEndpointCoachmark.closeBtnAriaLabel', defaultMessage: 'Close' },
    primaryBtnText: { id: 'luis.reviewEndpointCoachmark.primaryBtnText', defaultMessage: 'Take me there' },
    body: { id: 'luis.reviewEndpointCoachmark.body', defaultMessage: 'You can now label entities while reviewing endpoint utterances.' }
});

// This teaching bubble should be displayed in one month from now.
const EXPIRY_DATE = new Date('4/1/2021');

const useVisibilityState = () => {
    const { pathname } = useLocation();
    const { uiStore, dispatcher } = useGlobalState();
    const hasExpired = new Date() > EXPIRY_DATE;
    const isOutsideReviewPage = !doesRouteMatchLocation(routeTable.luis.reviewUtterancesPage(), { locationToMatch: pathname });
    const viewedBefore = uiStore.teachingBubbles.LabelReviewEndpointUtterances;
    const shouldBeVisible = isOutsideReviewPage && !viewedBefore && !hasExpired;

    const [isCoachmarkVisible, setIsCoachmarkVisible] = React.useState(shouldBeVisible);

    const dismiss = () => {
        setIsCoachmarkVisible(false);
        dispatcher.dispatch('markTeachingBubbleAsViewed', { bubbleName: 'LabelReviewEndpointUtterances' });
    };

    return [isCoachmarkVisible, dismiss] as const;
};

export const useReviewEndpointUtterancesCoachmark = () => {
    const history = useHistory();
    const { rootStore } = useGlobalState();
    const [isVisible, dismiss] = useVisibilityState();
    const { localize } = useLocalization();
    const ref = React.useRef<HTMLElement>();

    const appId = rootStore.currentLuisAppStore.id;
    const versionId = rootStore.currentLuisAppStore.activeVersion;

    const handleClick = () => {
        dismiss();
        history.push(routeTable.luis.reviewUtterancesPage(appId, versionId));
    };

    return {
        dismiss,
        ref,
        coachmark: isVisible && (
            <Coachmark
                persistentBeak
                target={ref.current}
                positioningContainerProps={{
                    directionalHint: DirectionalHint.rightCenter
                }}
                ariaAlertText={localize(messages.ariaAlertText)}>
                <TeachingBubbleContent
                    headline={localize(messages.headline)}
                    hasCloseButton
                    closeButtonAriaLabel={localize(messages.closeBtnAriaLabel)}
                    primaryButtonProps={{
                        text: localize(messages.primaryBtnText),
                        onClick: handleClick
                    }}
                    onDismiss={dismiss}>
                    {localize(messages.body)}
                </TeachingBubbleContent>
            </Coachmark>
        )
    };
};
